import React, {
  forwardRef,
  type HTMLInputTypeAttribute,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";
import type { BaseProps } from "@purpur/common-types";

import { TextFieldAiInternal } from "./text-field-ai";
import { TextFieldBase } from "./text-field-base";

export const textFieldVariants = ["default", "ai"] as const;
export type TextFieldVariant = (typeof textFieldVariants)[number];

type TextFieldCommonProps = {
  /**
   * Use to display e.g. a button before the text field.
   *
   * _NOTE: Should ideally only be used by other purpur components!_
   */
  beforeField?: ReactNode;
  /**
   * Use to display e.g. a button after the text field.
   *
   * _NOTE: Should ideally only be used by other purpur components!_
   */
  afterField?: ReactNode;
  /**
   * Use to display e.g. an icon at the end inside of the text field.
   *
   * _NOTE: Should ideally only be used by other purpur components!_
   */
  endAdornment?: ReactNode;
  /**
   * Use to render error message below the text field. The text field renders with error appearance.
   * */
  errorText?: string;
  /**
   * Use to give context about the field's input. Renders below the field.
   * */
  helperText?: string;
  /**
   * The label of the text field.
   * */
  label?: string;
  /**
   *  Use to render a spinner at the end inside of the text field.
   */
  loading?: boolean;
  /**
   * Hide the asterisk for required fields.
   * This is useful when the field is required but the context already indicates that.
   */
  hideRequiredAsterisk?: boolean;
  /**
   * Use to display e.g. an icon at the start inside of the text field.
   *
   * _NOTE: Should ideally only be used by other purpur components!_
   */
  startAdornment?: ReactNode;
  /**
   * Use to set the type of the field.
   */
  type?: Extract<
    HTMLInputTypeAttribute,
    "email" | "number" | "password" | "search" | "tel" | "text"
  >;
  /**
   * Use to render text field with valid appearance. A check
   * icon will render at the start inside of the input.
   */
  valid?: boolean;
  negative?: boolean;
};

type TextFieldClearProps =
  | {
      /**
       * An accessible label for the clear button.
       * */
      clearButtonAriaLabel: string;
      /**
       * Event handler called when the clear button is clicked.
       * */
      onClear: () => void;
    }
  | {
      /**
       * An accessible label for the clear button.
       * */
      clearButtonAriaLabel?: never;
      /**
       * Event handler called when the clear button is clicked.
       * */
      onClear?: never;
    };

type TextFieldAiProps = {
  /**
   * Animate prepopulated value once when the field scrolls into view.
   */
  animatePrepopulatedValue?: boolean;
  /**
   * Duration in milliseconds for the prepopulated typing animation.
   */
  typingAnimationDurationMs?: number;
};

export type TextFieldBaseProps = Omit<BaseProps<"input">, "type"> &
  TextFieldCommonProps &
  TextFieldClearProps;

export type TextFieldAiInternalProps = Omit<BaseProps<"input">, "type"> &
  TextFieldCommonProps &
  TextFieldClearProps &
  TextFieldAiProps;

export type TextFieldProps = Omit<BaseProps<"input">, "type"> &
  TextFieldCommonProps &
  TextFieldClearProps &
  TextFieldAiProps & {
    /**
     * Use to select the text field variant.
     * - `"default"` renders a standard text field.
     * - `"ai"` renders a text field with AI presentation (gradient border, AI icon, typing animation).
     * @default "default"
     */
    variant?: TextFieldVariant;
  };

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ variant = "default", ...props }, ref) => {
    if (variant === "ai") {
      return <TextFieldAiInternal ref={ref} {...props} />;
    }

    return <TextFieldBase ref={ref} {...props} />;
  }
);

TextField.displayName = "TextField";

export const isTextField = (child?: ReactNode): child is ReactElement<TextFieldProps> =>
  isValidElement<TextFieldProps>(child) &&
  (child.type as any).displayName === TextField.displayName; // eslint-disable-line @typescript-eslint/no-explicit-any
